import React, { useState } from 'react';
import { StyleSheet, View, Image, Text, Pressable, TouchableOpacity } from 'react-native-web';
import approveIcon from "../icons/DupeIcons-17-Biz-Available.png";
import denyIcon from "../icons/DupeIcons-16-Biz-Unavailable.png";
import photoIcon from "../icons/DupeIcons-14-Photo.png";
import videoIcon from "../icons/DupeIcons-12-Video.png";
import emailIcon from "../icons/DupeIcons-Mail.png";
import messageIcon from "../icons/blue-send-icon.png";
import downArrowIcon from "../icons/DupeIcons-03-Arrow.png";
import shippingIcon from "../icons/shipping-icon.jpg";
import Button from './Button';
import StarRating from './StarRating';
import CreatorReviewForm from './CreatorReviewForm';
import { getCreatorAddress } from '../services/BusinessService';
import toast from 'react-hot-toast';

const CreatorPreview = ({
    creator,
    variant = "active",
    // Event handlers
    onCreatorPress,
    onAestheticPress,
    onMessagePress,
    onEmailCopy,
    onContentModalOpen,
    onUpdateDropdown,
    onUpdateModal,
    onDenyApplicant,
    onReviewToggle,
    // State
    expandedCreatorReviewId,
    // Campaign data
    campaign,
    businessId,
    // UI state
    isInvitingAll = false
}) => {
    const [isLoadingAddress, setIsLoadingAddress] = useState(false);

    const handleCreatorPress = () => {
        if (onCreatorPress) {
            onCreatorPress(creator);
        }
    };

    const handleAddressCopy = async () => {
        if (isLoadingAddress) return;

        setIsLoadingAddress(true);
        try {
            const response = await getCreatorAddress(creator.id);
            if (response && response.status === 200 && response.data) {
                const address = response.data;
                // Format address as a string
                const addressLines = [
                    address.addressLine1,
                    address.addressLine2,
                    address.city,
                    address.region,
                    address.zipCode,
                    address.country
                ].filter(Boolean); // Remove null/undefined values

                const fullAddress = addressLines.join(', ');

                // Copy to clipboard
                await navigator.clipboard.writeText(fullAddress);
                toast.success('Address copied to clipboard!');
            } else {
                toast.error('Address not available');
            }
        } catch (err) {
            console.error('Error fetching address:', err);
            toast.error('Failed to fetch address');
        } finally {
            setIsLoadingAddress(false);
        }
    };

    const handleAestheticPress = (aesthetic) => {
        if (onAestheticPress) {
            onAestheticPress(creator, aesthetic);
        }
    };

    const handleMessagePress = () => {
        if (creator.channel && creator.channel._url && onMessagePress) {
            onMessagePress(creator.channel._url);
        } else if (onMessagePress) {
            onMessagePress(creator);
        }
    };

    const handleEmailCopy = () => {
        if (onEmailCopy && creator.preferred_email) {
            onEmailCopy(creator.preferred_email);
        }
    };

    const handleContentModalOpen = () => {
        if (onContentModalOpen) {
            onContentModalOpen(creator);
        }
    };

    const handleUpdateDropdown = (event) => {
        if (onUpdateDropdown) {
            onUpdateDropdown(event, creator.id);
        }
    };

    const handleUpdateModal = (updateType) => {
        if (onUpdateModal) {
            onUpdateModal(creator, updateType);
        }
    };

    const handleDenyApplicant = () => {
        if (onDenyApplicant) {
            onDenyApplicant(creator);
        }
    };

    const handleReviewToggle = () => {
        if (onReviewToggle) {
            const newExpandedId = expandedCreatorReviewId === creator.id ? null : creator.id;
            onReviewToggle(newExpandedId);
        }
    };

    const renderCreatorInfo = () => (
        <Pressable 
            style={({ hovered }) => [
                styles.creatorInfoSection,
                hovered && styles.creatorInfoSectionHovered
            ]}
            onPress={handleCreatorPress}
            accessible={true}
            accessibilityLabel={`View ${creator.first_name} ${creator.last_name} profile`}
            accessibilityRole="button"
        >
            <Image
                source={{uri: "https://d3p3fw3rutb1if.cloudfront.net/users-preview/" + creator.id}}
                style={styles.creatorPreviewImage}
                accessible={true}
                accessibilityLabel={`${creator.first_name} ${creator.last_name} profile picture`}
            />
            <View style={styles.creatorDetails}>
                <View style={styles.creatorHeader}>
                    <View style={styles.creatorNameRow}>
                        <Text style={styles.creatorName} numberOfLines={1}>
                            {creator.first_name} {creator.last_name}
                        </Text>
                        {creator.country && (
                            <View style={styles.countryContainer}>
                                <Text style={styles.creatorCountryText} numberOfLines={1}>
                                    {creator.country}
                                </Text>
                                {variant === "active" && creator.share_info_with_brands && (
                                    <Pressable
                                        onPress={handleAddressCopy}
                                        style={({ hovered }) => [
                                            styles.addressIconButton,
                                            hovered && styles.addressIconButtonHovered,
                                            isLoadingAddress && styles.addressIconButtonDisabled
                                        ]}
                                        disabled={isLoadingAddress}
                                        accessible={true}
                                        accessibilityLabel="Copy creator address to clipboard"
                                        accessibilityRole="button"
                                    >
                                        <Image source={shippingIcon} style={styles.addressIcon} />
                                    </Pressable>
                                )}
                            </View>
                        )}
                    </View>
                    <View style={styles.contentTypeIcons}>
                        {creator.video && <Image source={videoIcon} style={styles.contentIcon} />}
                        {creator.photo && <Image source={photoIcon} style={styles.contentIcon} />}
                    </View>
                </View>
                {creator.aesthetics && creator.aesthetics.length > 0 && (
                    <View style={styles.aestheticsContainer}>
                        {creator.aesthetics.map((aesthetic, index) => (
                            <Pressable 
                                key={index}
                                style={({ hovered }) => [
                                    styles.aestheticTag,
                                    hovered && styles.aestheticTagHovered
                                ]}
                                onPress={() => handleAestheticPress(aesthetic)}
                                accessible={true}
                                accessibilityLabel={`View ${aesthetic} content`}
                                accessibilityRole="button"
                            >     
                                <Text style={styles.aestheticText} numberOfLines={1}>{aesthetic}</Text>
                            </Pressable>
                        ))}
                    </View>
                )}
                <View style={styles.ratingsContainer}>
                    {(creator.avg_quality_rating !== null && creator.avg_responsiveness_rating !== null) && 
                     (creator.avg_quality_rating !== undefined && creator.avg_responsiveness_rating !== undefined) ? (
                        <View style={styles.ratingsRow}>
                            <StarRating rating={creator.avg_responsiveness_rating} label="Responsiveness" />
                            <StarRating rating={creator.avg_quality_rating} label="Quality" />
                        </View>
                    ) : (
                        <Text style={styles.noRatingText}>This creator has not been rated yet.</Text>
                    )}
                </View>
            </View>
        </Pressable>
    );

    const renderMessagingSection = () => {
        if (variant !== "active" && variant !== "complete") return null;

        return (
            <>
                {creator.lastMessage ? (
                    <View style={styles.lastMessageView}>
                        <Text style={styles.lightBlueText}>Last Message:</Text>
                        <Pressable onPress={handleMessagePress}>
                            <Text style={styles.lightGreyText}>{creator.lastMessage}</Text>
                        </Pressable>
                    </View>
                ) : (
                    <View style={styles.lastMessageView}>
                        <Pressable onPress={handleMessagePress}>
                            <Text style={styles.lightBlueText}>No Messages yet</Text>
                        </Pressable>
                    </View>
                )}
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                    {creator.preferred_email && (
                        <Pressable onPress={handleEmailCopy}>
                            <Image source={emailIcon} style={styles.emailIcon} />
                        </Pressable>
                    )}
                    <Pressable onPress={handleMessagePress}>
                        <Image source={messageIcon} style={styles.messageIcon} />
                    </Pressable>
                </View>
            </>
        );
    };

    const renderUploadsSection = () => {
        if (variant !== "active" && variant !== "complete") return null;

        return (
            <View style={styles.lastMessageView}>
                <Text style={styles.lightBlueText}>Uploads</Text>
                <Pressable onPress={handleContentModalOpen}>
                    <View>
                        <Text style={styles.lightBlueText}>{creator.photo_uploads?.length || 0} Photos</Text>
                    </View>
                    <View>
                        <Text style={styles.lightBlueText}>{creator.video_uploads?.length || 0} Videos</Text>
                    </View>
                </Pressable>
            </View>
        );
    };

    const renderActiveVariantActions = () => {
        if (variant !== "active") return null;

        return (
            <View>
                {!creator.lastMessage && (
                    <Text style={[styles.boldRedText, styles.bottomMargin5]}>Not Messaged</Text>
                )}
                {creator.lastMessage && (
                    <Text style={[styles.lightBlueText, styles.bottomMargin5]}>Contacted</Text>
                )}
                <select 
                    value={creator.status} 
                    style={styles.dropdown} 
                    className={"Dropdown-root"} 
                    placeholder={"Select..."} 
                    onChange={handleUpdateDropdown}
                >
                    <option value="accepted">Accepted</option>
                    <option value="contract_sent">Contract Sent</option>
                    <option value="complete">Complete</option>
                </select>
            </View>
        );
    };

    const renderCompleteVariantActions = () => {
        if (variant !== "complete") return null;

        return (
            <>
                {creator.paid_amount > 0 && (
                    <View style={{marginRight: 15}}>
                        <Text style={styles.latoBlueText}>${creator.paid_amount} Paid</Text>
                    </View>
                )}
                <TouchableOpacity
                    style={styles.reviewCreatorButton}
                    onPress={handleReviewToggle}
                >
                    <Text style={styles.reviewCreatorText}>Review Creator</Text>
                    <Image
                        source={downArrowIcon}
                        style={[
                            styles.reviewExpandIcon,
                            expandedCreatorReviewId === creator.id && styles.reviewExpandIconRotated
                        ]}
                    />
                </TouchableOpacity>
            </>
        );
    };

    const renderAppliedVariantActions = () => {
        if (variant !== "applied") return null;

        return (
            <>
                <Text style={styles.notesInput}>{creator.creator_notes}</Text>
                <View style={{flexDirection: 'row'}}>
                    <Image 
                        source={approveIcon} 
                        style={styles.largeIcon} 
                        onClick={() => handleUpdateModal("Accept")}
                    />
                    {creator.status !== "DENIED" && (
                        <Image 
                            source={denyIcon} 
                            style={styles.largeIcon} 
                            onClick={handleDenyApplicant} 
                        />
                    )}
                </View>
            </>
        );
    };

    const renderShortlistedVariantActions = () => {
        if (variant !== "shortlisted") return null;

        return (
            <View style={{height: 80, justifyContent: 'space-between'}}>
                <Button 
                    text={"Invite"} 
                    variant={"tertiary"} 
                    onPress={() => handleUpdateModal("Invite")} 
                />
                <Button 
                    text={"Remove"} 
                    variant={"warning"} 
                    onPress={() => handleUpdateModal("Remove")} 
                />
            </View>
        );
    };

    const renderReviewForm = () => {
        if (variant !== "complete" || expandedCreatorReviewId !== creator.id) return null;

        return (
            <View style={styles.creatorReviewContainer}>
                <CreatorReviewForm
                    creator={creator}
                    showCreatorInfo={false}
                    campaignId={campaign?.id}
                    businessId={businessId}
                />
            </View>
        );
    };

    return (
        <View>
            <View style={styles.creatorPreviewContainer}>
                <View style={styles.creatorMainContent}>
                    {renderCreatorInfo()}
                    <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1}}>
                        {renderMessagingSection()}
                        {renderUploadsSection()}
                        {renderActiveVariantActions()}
                        {renderCompleteVariantActions()}
                        {renderAppliedVariantActions()}
                        {renderShortlistedVariantActions()}
                    </View>
                </View>
            </View>
            {renderReviewForm()}
        </View>
    );
};

const styles = StyleSheet.create({
    creatorPreviewContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        marginLeft: '2.5%',
        marginRight: '2.5%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        transition: 'all 0.2s ease',
    },
    creatorMainContent: {
        flexDirection: 'row',
        flex: 1,
        alignItems: 'stretch',
    },
    creatorInfoSection: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        marginRight: 20,
        borderRadius: 8,
        padding: 8,
        transition: 'all 0.2s ease',
        outlineStyle: 'none',
        borderWidth: 0,
    },
    creatorInfoSectionHovered: {
        backgroundColor: '#f9fafb',
        transform: [{ scale: 1.02 }],
    },
    creatorPreviewImage: {
        width: 80,
        height: 80,
        borderRadius: 40,
        marginRight: 16,
        backgroundColor: '#f3f4f6',
        borderWidth: 2,
        borderColor: '#e5e7eb',
    },
    creatorDetails: {
        flex: 1,
        justifyContent: 'center',
    },
    creatorHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    creatorNameRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        flex: 1,
        marginRight: 12,
    },
    creatorName: {
        color: '#396de0',
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 20,
        fontWeight: '700',
        marginRight: 8,
    },
    countryContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    creatorCountryText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: "#6b7280",
        paddingVertical: 4,
    },
    addressIconButton: {
        padding: 4,
        borderRadius: 4,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    addressIconButtonHovered: {
        backgroundColor: '#f3f4f6',
        transform: [{ scale: 1.1 }],
    },
    addressIconButtonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    addressIcon: {
        width: 28,
        height: 28,
        resizeMode: 'contain',
    },
    ratingsContainer: {
        marginTop: 8,
    },
    ratingsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 15,
    },
    noRatingText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: "#9ca3af",
        fontStyle: 'italic',
        fontWeight: '400',
    },
    contentTypeIcons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    contentIcon: {
        width: 20,
        height: 20,
        tintColor: '#6b7280',
    },
    latoBlueText: {
        color: '#396de0',
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        fontWeight: 500,
        marginTop: 5,
    },
    aestheticsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginBottom: 8,
        gap: 6,
    },
    aestheticTag: {
        backgroundColor: '#f3f4f6',
        borderRadius: 12,
        paddingVertical: 4,
        paddingHorizontal: 8,
        transition: 'all 0.2s ease',
        outlineStyle: 'none',
        borderWidth: 0,
    },
    aestheticTagHovered: {
        backgroundColor: '#e5e7eb',
        transform: [{ scale: 1.05 }],
    },
    aestheticText: {
        fontSize: 12,
        color: '#6b7280',
        fontFamily: "'lato', sans-serif",
        fontWeight: '500',
    },
    largeIcon: {
        width: 30,
        height: 30,
        marginRight: 5,
        cursor: 'pointer'
    },
    notesInput: {
        padding: 10,
        width: '30%',
        display: 'flex',
        color: 'grey',
        fontFamily: "'lato', sans-serif",
        borderWidth: 1,
        borderColor: '#b8cbf5',
        borderRadius: 5,
    },
    dropdown: {
        fontFamily: "lato, sans-serif",
        borderColor: '#396DE0',
        backgroundColor: '#F1F1F1',
        borderRadius: 5,
        padding: 10,
    },
    emailIcon: {
        width: 20,
        height: 12,
    },
    messageIcon: {
        width: 20,
        height: 20,
        marginLeft: 10,
    },
    lightBlueText: {
        color: '#b8cbf5',
        fontWeight: 'bold',
    },
    lightGreyText: {
        color: '#b2b2b2',
    },
    lastMessageView: {
        width: 150,
        maxHeight: 150,
        overflow: 'hidden',
    },
    boldRedText: {
        color: '#e8b5b5',
        fontWeight: 'bold',
    },
    bottomMargin5: {
        marginBottom: 5,
    },
    reviewCreatorButton: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 10,
        borderColor: '#b8cbf5',
        borderRadius: 5,
        cursor: 'pointer',
    },
    reviewCreatorText: {
        color: '#396de0',
        fontFamily: "'lato', sans-serif",
        fontWeight: 'bold',
        marginRight: 10,
    },
    reviewExpandIcon: {
        width: 20,
        height: 20,
    },
    reviewExpandIconRotated: {
        transform: [{rotate: '180deg'}],
    },
    creatorReviewContainer: {
        padding: 20,
        backgroundColor: '#f8f9ff',
        marginLeft: '2.5%',
        marginRight: '2.5%',
        marginBottom: 16,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
});

export default CreatorPreview; 